import type { CatalogSort, Filterable, Offered } from '@/entities/catalog';
import type { AccessibleBoard } from '@/entities/collab';
import type { Database } from '@/entities/database';

/** What the home dashboard lets the viewer filter by: everything, including the workspace kind. */
export const HOME_OFFERED: Offered = {
  type: true,
  favorites: true,
  recent: true,
  owner: true,
  space: true,
};

/** The orders of the home: boards (and databases by name); the manual order is a workspace's. */
export const HOME_SORTS: readonly CatalogSort[] = ['opened', 'updated', 'name'];

/** How a board is seen by the shared filters and search (name, workspace and label names). */
export const boardFilterable = (
  board: AccessibleBoard,
  opened: ReadonlySet<string>,
): Filterable => ({
  kind: 'board',
  favorite: board.is_favorite,
  recent: opened.has(board.id),
  ownerId: board.owner_user_id,
  space: board.workspace_type === 'personal' ? 'personal' : 'team',
  haystack: [
    board.name.toLocaleLowerCase(),
    (board.workspace_name ?? '').toLocaleLowerCase(),
    ...board.labels.map((label) => label.name.toLocaleLowerCase()),
  ],
});

/** How a database is seen by the shared filters and search (name and workspace name). */
export const databaseFilterable = (
  database: Database,
  opened: ReadonlySet<string>,
  spaceOf: ReadonlyMap<string, 'personal' | 'team'>,
  workspaceNameOf: ReadonlyMap<string, string>,
): Filterable => ({
  kind: 'database',
  favorite: database.is_favorite,
  recent: opened.has(database.id),
  ownerId: database.owner_user_id,
  space: spaceOf.get(database.workspace_id) ?? 'team',
  haystack: [
    database.name.toLocaleLowerCase(),
    (workspaceNameOf.get(database.workspace_id) ?? '').toLocaleLowerCase(),
  ],
});

/** The catalog address, carrying the search text when there is one (the catalogs read `?q=`). */
export const catalogLink = (path: string, query: string): string =>
  query.trim() === '' ? path : `${path}?q=${encodeURIComponent(query.trim())}`;
